import React, { createContext, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login/Login';
import Dashboard from './pages/Dashboard/Dashboard';
import ListAdmin from './pages/ListAdmin/ListAdmin';
import Setting from './pages/Setting/Setting';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { getLocalStorage } from './utils/storages';
import MessageError from './components/atomics/MessageError';
import ListFeedback from './pages/ListFeedback/ListFeedback';

const queryClient = new QueryClient();

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const token = getLocalStorage('access_token');
  const userData = (() => {
    try {
      return JSON.parse(getLocalStorage('user_data') || '{}');
    } catch {
      return {};
    }
  })();

  if (!token || Number(userData.level) > 2) return <Navigate to="/" replace />;
  return <>{children}</>;
};

const CXRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const token = getLocalStorage('access_token');
  const userData = (() => {
    try {
      return JSON.parse(getLocalStorage('user_data') || '{}');
    } catch {
      return {};
    }
  })();

  if (!token || Number(userData.level) > 3) return <Navigate to="/list-feedback" replace />;
  return <>{children}</>;
};

const PrivateSuperadminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const token = getLocalStorage('access_token');
  const userData = (() => {
    try {
      return JSON.parse(getLocalStorage('user_data') || '{}');
    } catch {
      return {};
    }
  })();

  if (!token || Number(userData.level) > 0) return <Navigate to="/" replace />;
  return <>{children}</>;
};

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const token = getLocalStorage('access_token');
  const userData = (() => {
    try {
      return JSON.parse(getLocalStorage('user_data') || '{}');
    } catch {
      return {};
    }
  })();

  if (token && Number(userData.level) < 3) return <Navigate to="/dashboard" replace />;
  if (token && Number(userData.level) === 3) return <Navigate to="/list-feedback" replace />;
  return <>{children}</>;
};

// Define the AppContextType interface according to your context needs
export interface AppContextType {
  admin?: object | undefined;
  errorMessage?: string;
  setErrorMessage?: React.Dispatch<React.SetStateAction<string>>;
}

export const AppContext = createContext<AppContextType | undefined>(undefined);

const App: React.FC = () => {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [contentModal, setContentModal] = useState<React.ReactNode>();
  const [admin, setAdmin] = useState<object | undefined>(undefined);

  useEffect(() => {
    if (errorMessage) {
      setTimeout(() => {
        setErrorMessage('');
      }, 3000);
    }
  }, [errorMessage]);

  useEffect(() => {
    const adminData = getLocalStorage('user_data');

    if (adminData) {
      setAdmin(JSON.parse(adminData));
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AppContext.Provider value={{ admin, errorMessage, setErrorMessage }}>
        {
          errorMessage && (
            <MessageError>
              {errorMessage}
            </MessageError>
          )
        }
        {
          contentModal && contentModal
        }
        <div>
          <Router>
            <Routes>
              <Route
                path="/"
                element={
                  <PublicRoute>
                    <Login />
                  </PublicRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute>
                    <Dashboard />
                  </PrivateRoute>
                }
              />
              <Route
                path="/list-admin"
                element={
                  <PrivateSuperadminRoute>
                    <ListAdmin />
                  </PrivateSuperadminRoute>
                }
              />
              <Route
                path="/list-feedback"
                element={
                  <CXRoute>
                    <ListFeedback />
                  </CXRoute>
                }
              />
              <Route
                path="/setting"
                element={
                  <CXRoute>
                    <Setting />
                  </CXRoute>
                }
              />
            </Routes>
          </Router>
        </div>
      </AppContext.Provider>
    </QueryClientProvider>
  );
};

export default App;